import type React from 'react';
import { useEffect, useState } from 'react';
import { performanceMonitor } from '../utils/performance-monitor';

interface PerformanceStats {
  [componentName: string]: {
    count: number;
    avgTime: number;
    maxTime: number;
  };
}

/**
 * パフォーマンス統計を表示するダッシュボードコンポーネント
 * 開発環境でのみ表示される
 */
export const PerformanceDashboard: React.FC = () => {
  const [stats, setStats] = useState<PerformanceStats>({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // 開発環境でのみ統計を更新
    if (process.env.NODE_ENV === 'development') {
      const interval = setInterval(() => {
        setStats(performanceMonitor.getPerformanceStats());
      }, 2000); // 2秒ごとに更新

      return () => clearInterval(interval);
    }
    return undefined;
  }, []);

  // 本番環境では何も表示しない
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  if (!isVisible) {
    return (
      <button
        type="button"
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 bg-blue-600 text-white px-3 py-2 rounded-lg text-xs z-50 hover:bg-blue-700"
      >
        パフォーマンス
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-50 max-w-md">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">パフォーマンス統計</h3>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => {
              performanceMonitor.clear();
              setStats({});
            }}
            className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
          >
            クリア
          </button>
          <button
            type="button"
            onClick={() => setIsVisible(false)}
            className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
          >
            ×
          </button>
        </div>
      </div>

      <div className="space-y-2 text-xs">
        {Object.entries(stats).length === 0 ? (
          <p className="text-gray-500">データがありません</p>
        ) : (
          Object.entries(stats).map(([componentName, stat]) => (
            <div key={componentName} className="bg-gray-50 p-2 rounded">
              <div className="font-medium">{componentName}</div>
              <div className="text-gray-600">
                <div>レンダリング回数: {stat.count}</div>
                <div>平均時間: {stat.avgTime.toFixed(2)}ms</div>
                <div>最大時間: {stat.maxTime.toFixed(2)}ms</div>
                <div className={`${stat.avgTime > 16 ? 'text-red-600' : 'text-green-600'}`}>
                  {stat.avgTime > 16 ? '⚠️ 遅い' : '✅ 良好'}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
